import { BlobImage } from '@/components/blob/BlobImage';

import type { PanelArtifact } from '../../../../../bridge/types/messages.ts';

import { ContentArtifactIllustration } from './ContentArtifactIllustration.tsx';
import { MessageRow } from './MessageRow.tsx';

export type ContentArtifactType =
	| 'post'
	| 'digital_ad'
	| 'email_html'
	| 'email_plaintext'
	| 'ig_comment_reply';

const TYPE_LABEL: Record<ContentArtifactType, string> = {
	post: 'Content Review',
	digital_ad: 'Ad Review',
	email_html: 'Email Review',
	email_plaintext: 'Email Review',
	ig_comment_reply: 'Reply Review',
};

const AMBIENT_GLOW: Record<
	ContentArtifactType,
	{ glow1: string; glow2: string }
> = {
	post: {
		glow1: 'rgba(147,51,234,0.07)',
		glow2: 'rgba(34,197,94,0.07)',
	},
	digital_ad: {
		glow1: 'rgba(234,88,12,0.07)',
		glow2: 'rgba(168,85,247,0.07)',
	},
	email_html: {
		glow1: 'rgba(239,68,68,0.07)',
		glow2: 'rgba(6,182,212,0.07)',
	},
	email_plaintext: {
		glow1: 'rgba(239,68,68,0.07)',
		glow2: 'rgba(6,182,212,0.07)',
	},
	ig_comment_reply: {
		glow1: 'rgba(20,184,166,0.07)',
		glow2: 'rgba(234,179,8,0.07)',
	},
};

export const ContentArtifactCard = ({
	message,
	onClick,
}: {
	message: PanelArtifact & { artifact_type: ContentArtifactType };
	onClick: () => void;
}) => {
	const artifactType = message.artifact_type;
	const glow = AMBIENT_GLOW[artifactType];
	const isInstagramPost = artifactType === 'post';
	const title = isInstagramPost
		? message.title || 'New Instagram Post'
		: message.title || message.description || 'Ready for review';
	const label = isInstagramPost
		? 'Click to edit & publish'
		: TYPE_LABEL[artifactType];
	const thumbnailUrl = message.icon_url;

	return (
		<MessageRow data-testid={`panel-artifact-${message.id}`}>
			<div className="flex w-full max-w-xs flex-col">
				<button
					type="button"
					onClick={onClick}
					className="glass-card-sm glass-card-hover group relative flex w-full
						cursor-pointer flex-col overflow-hidden text-left"
				>
					<div
						className="relative flex h-[120px] items-center justify-center
							overflow-hidden border-b border-[#F0F1F4] bg-[#F8F9FC]"
					>
						{thumbnailUrl ? (
							<BlobImage
								src={thumbnailUrl}
								alt=""
								className="absolute inset-0 size-full object-cover"
							/>
						) : (
							<>
								<div
									className="pointer-events-none absolute top-[-30px] left-[5%]
										h-[120px] w-[120px] rounded-full blur-[30px]"
									style={{ background: glow.glow1 }}
								/>
								<div
									className="pointer-events-none absolute top-[40px] left-[60%]
										h-[70px] w-[70px] rounded-full blur-[24px]"
									style={{ background: glow.glow2 }}
								/>
								<div className="relative z-10">
									<ContentArtifactIllustration type={artifactType} />
								</div>
							</>
						)}
					</div>
					<div className="flex flex-col gap-1 px-5 pt-4 pb-5">
						<span
							className="text-[15px] leading-tight font-bold
								text-(--text-primary)"
						>
							{title}
						</span>
						<span className="text-[11px] font-medium text-(--text-muted)">
							{label}
						</span>
					</div>
				</button>
			</div>
		</MessageRow>
	);
};
